/**
 * Storage stub for the `job-documents` / `job-photos` buckets.
 *
 * Nothing is stored. Uploads succeed and hand back the path the caller asked
 * for (call sites write that path into a row), and signed URLs resolve to a
 * 1x1 transparent PNG so an `<img>` that gets one renders blank instead of
 * showing a broken-image icon.
 */

const BLANK_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk'
  + 'YPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

/** Paths that were "uploaded" this session, per bucket — handy for debugging. */
export type FakeStorage = ReturnType<typeof createFakeStorage>;

export function createFakeStorage() {
  const uploads = new Map<string, Set<string>>();

  const bucketApi = (bucket: string) => {
    const seen = uploads.get(bucket) ?? new Set<string>();
    uploads.set(bucket, seen);
    return {
      upload: async (path: string, _file?: unknown, _opts?: unknown) => {
        seen.add(path);
        return { data: { id: path, path, fullPath: `${bucket}/${path}` }, error: null };
      },
      update: async (path: string, _file?: unknown, _opts?: unknown) => {
        seen.add(path);
        return { data: { id: path, path, fullPath: `${bucket}/${path}` }, error: null };
      },
      remove: async (paths: string[]) => {
        for (const p of paths) seen.delete(p);
        return { data: paths.map((p) => ({ name: p })), error: null };
      },
      createSignedUrl: async (path: string, _expiresIn?: number) => ({
        data: { signedUrl: BLANK_PNG, path },
        error: null,
      }),
      createSignedUrls: async (paths: string[], _expiresIn?: number) => ({
        data: paths.map((p) => ({ path: p, signedUrl: BLANK_PNG, error: null })),
        error: null,
      }),
      getPublicUrl: (path: string) => ({ data: { publicUrl: BLANK_PNG, path } }),
      list: async () => ({ data: Array.from(seen).map((name) => ({ name })), error: null }),
    };
  };

  return {
    from: bucketApi,
    /** Exposed for tests/debugging only. */
    _uploads: uploads,
  };
}

export { BLANK_PNG };
